import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArtistsAPI } from '../../api/ArtistsAPI';
import './SpotifyLinkReview.css';

// Asks Spotify for every artist that has no Spotify ID yet and lets the admin
// confirm each match. The app links Spotify's top hit on its own when a host
// adds an act; this does the same for the backlog, but a person decides:
// Spotify ranks by popularity, so the top hit for a local DJ is often a
// better-known act with a similar name. "Übernehmen" stores the Spotify ID;
// "Passt nicht" stores a "no Spotify" mark, so the artist stays out of the next
// review and no import or host suggestion links it behind the admin's back.

const LOOKUPS_IN_FLIGHT = 2;

// de-DE, not de-AT: current CLDR groups Austrian numbers with a space, "1.200" reads better here.
const number = (n) => new Intl.NumberFormat('de-DE').format(n);
const spotifyUrl = (candidate) => candidate?.url || `https://open.spotify.com/artist/${candidate?.spotifyId}`;

/** Why a lookup failed, with the HTTP status so the cause is visible without the browser console. */
export const searchErrorText = (httpStatus) => {
  if (httpStatus === 404) return 'Spotify-Suche fehlgeschlagen (HTTP 404): das Backend kennt den Endpoint nicht, es läuft noch eine ältere Version.';
  if (httpStatus === 401 || httpStatus === 403) return `Spotify-Suche fehlgeschlagen (HTTP ${httpStatus}): keine Berechtigung, bitte neu anmelden.`;
  if (httpStatus >= 500) return `Spotify-Suche fehlgeschlagen (HTTP ${httpStatus}): Fehler im Backend, etwa Spotify nicht konfiguriert oder nicht erreichbar.`;
  return httpStatus ? `Spotify-Suche fehlgeschlagen (HTTP ${httpStatus}).` : 'Spotify-Suche fehlgeschlagen.';
};

/** The candidate to show first: the one bearing the artist's name, else Spotify's top hit. */
export const preferredCandidate = (candidates) => Math.max(0, candidates.findIndex((c) => c?.nameMatches));

export default function SpotifyLinkReview({ artists, onClose, onUpdated }) {
  // The list is fixed when the dialog opens; linking an artist must not restart the search.
  const [queue] = useState(() => artists);
  const [rows, setRows] = useState(() => queue.map((artist) => ({ artist, status: 'queued', candidates: [], pick: 0 })));
  const [running, setRunning] = useState(queue.length > 0);

  const patchRow = useCallback((index, changes) =>
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...changes } : row))), []);

  const lookup = useCallback(async (index, isCancelled = () => false) => {
    patchRow(index, { status: 'searching' });
    try {
      const found = await ArtistsAPI.spotifyCandidates(queue[index].name);
      if (isCancelled()) return;
      const candidates = Array.isArray(found) ? found.filter((c) => c?.spotifyId) : [];
      patchRow(index, candidates.length
        ? { status: 'open', candidates, pick: preferredCandidate(candidates) }
        : { status: 'none', candidates: [] });
    } catch (err) {
      console.error('Spotify-Suche fehlgeschlagen:', err);
      if (!isCancelled()) patchRow(index, { status: 'error', httpStatus: err?.response?.status });
    }
  }, [queue, patchRow]);

  useEffect(() => {
    let cancelled = false;
    const isCancelled = () => cancelled;
    const pending = queue.map((_, i) => i);
    const worker = async () => {
      while (!cancelled && pending.length) await lookup(pending.shift(), isCancelled);
    };
    Promise.all(Array.from({ length: LOOKUPS_IN_FLIGHT }, worker)).then(() => { if (!cancelled) setRunning(false); });
    return () => { cancelled = true; };
  }, [queue, lookup]);

  const counts = useMemo(() => rows.reduce((acc, row) => ({ ...acc, [row.status]: (acc[row.status] || 0) + 1 }), {}), [rows]);
  const checked = rows.length - (counts.queued || 0) - (counts.searching || 0);

  // Writes the decision to the artist; `changes` is what the review decided, `after` the row state on success.
  const save = async (index, changes, after) => {
    const row = rows[index];
    patchRow(index, { status: 'saving', error: '' });
    try {
      const updated = await ArtistsAPI.update(row.artist.id, {
        name: row.artist.name,
        instagram: row.artist.instagram ?? null,
        spotifyId: null,
        ...changes,
      });
      patchRow(index, after);
      onUpdated?.(updated && typeof updated === 'object' ? updated : { ...row.artist, ...changes });
    } catch (err) {
      console.error('Änderung am Künstler konnte nicht gespeichert werden:', err);
      patchRow(index, { status: 'open', error: 'Speichern fehlgeschlagen – bitte noch einmal versuchen.' });
    }
  };

  const accept = (index) => {
    const candidate = rows[index].candidates[rows[index].pick];
    if (candidate) save(index, { spotifyId: candidate.spotifyId }, { status: 'linked', linked: candidate });
  };
  // Rows whose shown hit bears the artist's name: the admin can take them all in one go.
  const matchingOpen = rows
    .map((row, index) => (row.status === 'open' && row.candidates[row.pick]?.nameMatches ? index : -1))
    .filter((index) => index >= 0);
  const [acceptingAll, setAcceptingAll] = useState(false);
  const acceptAllMatching = async () => {
    setAcceptingAll(true);
    // One after another, so the backend sees one save at a time.
    for (const index of matchingOpen) {
      const candidate = rows[index].candidates[rows[index].pick];
      await save(index, { spotifyId: candidate.spotifyId }, { status: 'linked', linked: candidate });
    }
    setAcceptingAll(false);
  };

  const reject = (index) => save(index, { noSpotify: true }, { status: 'skipped' });
  const reopen = (index) => save(index, { noSpotify: false }, { status: 'open' });

  const renderMatch = (row, index) => {
    switch (row.status) {
      case 'queued':
        return <span className="spl-muted">Wartet…</span>;
      case 'searching':
        return <span className="spl-muted"><span className="spl-spinner" aria-hidden="true" /> Sucht auf Spotify…</span>;
      case 'none':
        return <span className="spl-muted">Kein Treffer auf Spotify.</span>;
      case 'error':
        return (
          <span className="spl-muted">
            <span className="spl-error">{searchErrorText(row.httpStatus)}</span>
            <button type="button" className="spl-link" onClick={() => lookup(index)}>Erneut versuchen</button>
          </span>
        );
      case 'linked':
        return (
          <span className="spl-linked">
            Verknüpft mit <a href={spotifyUrl(row.linked)} target="_blank" rel="noreferrer">{row.linked.name}</a>
            <code>{row.linked.spotifyId}</code>
          </span>
        );
      case 'skipped':
        return (
          <span className="spl-muted">
            Als „kein Spotify“ gespeichert.
            <button type="button" className="spl-link" onClick={() => reopen(index)}>Rückgängig</button>
          </span>
        );
      default: {
        const candidate = row.candidates[row.pick];
        const saving = row.status === 'saving';
        const meta = [
          typeof candidate.followers === 'number' ? `${number(candidate.followers)} Follower` : '',
          (candidate.genres || []).slice(0, 3).join(', '),
        ].filter(Boolean).join(' · ');
        const alternatives = row.candidates.filter((_, i) => i !== row.pick);
        return (
          <div className="spl-candidate">
            {candidate.imageUrl
              ? <img className="spl-avatar" src={candidate.imageUrl} alt="" />
              : <span className="spl-avatar spl-avatar--empty" aria-hidden="true" />}
            <div className="spl-candidate-body">
              <div className="spl-candidate-name">
                <a href={spotifyUrl(candidate)} target="_blank" rel="noreferrer" title="Auf Spotify öffnen">{candidate.name}</a>
                {candidate.nameMatches
                  ? <span className="spl-badge spl-badge--ok">Name stimmt überein</span>
                  : <span className="spl-badge">Anderer Name</span>}
              </div>
              {meta && <div className="spl-candidate-meta">{meta}</div>}
              {alternatives.length > 0 && (
                <div className="spl-alternatives">
                  <span>Andere Treffer:</span>
                  {row.candidates.map((alt, i) => i !== row.pick && (
                    <button key={alt.spotifyId} type="button" className="spl-chip" onClick={() => patchRow(index, { pick: i })}>
                      {alt.name}{typeof alt.followers === 'number' ? ` (${number(alt.followers)})` : ''}
                    </button>
                  ))}
                </div>
              )}
              {row.error && <div className="spl-error">{row.error}</div>}
            </div>
            <div className="spl-actions">
              <button type="button" className="art-btn art-btn--save" onClick={() => accept(index)} disabled={saving}>
                {saving ? 'Speichert…' : 'Übernehmen'}
              </button>
              <button type="button" className="art-btn" onClick={() => reject(index)} disabled={saving}
                title="Merkt sich, dass dieser Künstler keinen passenden Spotify-Eintrag hat">
                Passt nicht
              </button>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div className="spl-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="spl-dialog" role="dialog" aria-modal="true" aria-labelledby="spl-title">
        <header className="spl-header">
          <div>
            <h3 id="spl-title" className="spl-title">Spotify-Abgleich</h3>
            <p className="spl-sub">
              {queue.length} Künstler ohne Spotify-ID. Spotify wird nach dem Namen gefragt, du entscheidest, ob der Treffer passt.
              „Passt nicht“ wird gespeichert, der Künstler taucht beim nächsten Abgleich nicht mehr auf.
            </p>
          </div>
          <button type="button" className="spl-close" onClick={onClose} aria-label="Dialog schließen">×</button>
        </header>

        <div className="spl-progress" role="status">
          {running && <span className="spl-spinner" aria-hidden="true" />}
          <span>{checked} von {queue.length} geprüft</span>
          <span className="spl-dot">·</span><span>{counts.linked || 0} verknüpft</span>
          <span className="spl-dot">·</span><span>{counts.skipped || 0} kein Spotify</span>
          <span className="spl-dot">·</span><span>{counts.none || 0} ohne Treffer</span>
        </div>

        <ul className="spl-list">
          {rows.map((row, index) => (
            <li key={row.artist.id} className={`spl-row spl-row--${row.status}`} data-testid={`spl-row-${row.artist.id}`}>
              <div className="spl-artist">
                <strong>{row.artist.name}</strong>
                {row.artist.instagram && (
                  <a className="art-ig-link" href={`https://instagram.com/${row.artist.instagram}`} target="_blank" rel="noreferrer">
                    @{row.artist.instagram}
                  </a>
                )}
              </div>
              <div className="spl-match">{renderMatch(row, index)}</div>
            </li>
          ))}
        </ul>

        <footer className="spl-footer">
          <button
            type="button"
            className="art-btn art-btn--save"
            onClick={acceptAllMatching}
            disabled={acceptingAll || matchingOpen.length === 0}
            title="Übernimmt jeden angezeigten Treffer, dessen Name mit dem Künstler übereinstimmt"
          >
            {acceptingAll ? 'Übernimmt…' : `Alle mit „Name stimmt überein“ übernehmen (${matchingOpen.length})`}
          </button>
          <button type="button" className="art-btn" onClick={onClose}>Schließen</button>
        </footer>
      </div>
    </div>
  );
}
